/**
 * Phase 7 — page-side form helpers built on top of the frozen Phase 5 module.
 *
 * This file is ADDITIVE. It does NOT modify
 * `frontend/src/utils/inputValidation.js` (Phase 5 frozen API).
 *
 * Rationale (sanitize-phase7.md §4.G):
 *   - Option A in the plan = inline parser per page; Option B = extend Phase 5
 *     module. We chose a third path: a sibling helper file. Phase 5 stays
 *     byte-equal; Phase 6 stays untouched; Phase 7 pages share one parser.
 *
 * Exports:
 *   - `parseServerInputError(response, options?)` — reads the Phase 3/4
 *     envelope {detail:{error_code, error, message, field?, limit?}} from
 *     any 4xx/5xx response, runs the user-facing string through
 *     `escapeForDisplay`, and returns a normalised object. Never throws.
 *   - `applySubmitGate(...)` — small declarative submit-gate.
 *   - `isFieldOk(value, profile, { required } )` — wraps `validateText` with
 *     the Phase 7 "optional-when-empty" rule.
 */

import { validateText, escapeForDisplay } from './inputValidation';

/**
 * @typedef {Object} ParsedServerError
 * @property {boolean} ok              True when the response is 2xx.
 * @property {number}  status          HTTP status (0 if we couldn't read it).
 * @property {string}  message         Always safe to render. Empty when ok.
 * @property {string=} errorCode       e.g. "INPUT_TOO_LONG".
 * @property {string=} field           Backend-reported field name.
 * @property {number=} limit           Backend-reported cap.
 */

/**
 * Parse a fetch-style `Response` (or any object with `.ok`, `.status`,
 * `.json()`) into a `ParsedServerError`. Always escapes the user-facing
 * `message` through `escapeForDisplay`.
 *
 * Phase 7 binding rule: callers MUST NOT auto-retry on any `INPUT_*` code,
 * including 429. The user must edit and click again.
 *
 * @param {{ok: boolean, status: number, json: () => Promise<any>}} response
 * @param {{fallback?: string}=} options
 * @returns {Promise<ParsedServerError>}
 */
export async function parseServerInputError(response, options) {
  const fallback = (options && options.fallback) || 'Request failed.';
  if (!response) {
    return { ok: false, status: 0, message: escapeForDisplay(fallback) };
  }
  if (response.ok) {
    return { ok: true, status: response.status || 200, message: '' };
  }
  let data = null;
  try {
    data = await response.json();
  } catch (_e) {
    // Body wasn't JSON. That's fine; fall through to fallback message.
  }
  const detail = data && typeof data === 'object' ? data.detail : null;
  const rawMessage =
    (detail && typeof detail === 'object' && (detail.message || detail.error)) ||
    (typeof detail === 'string' ? detail : null) ||
    (data && typeof data === 'object' && (data.message || data.error)) ||
    fallback;
  return {
    ok: false,
    status: response.status || 0,
    message: escapeForDisplay(String(rawMessage)),
    errorCode:
      detail && typeof detail === 'object' && typeof detail.error_code === 'string'
        ? detail.error_code
        : undefined,
    field:
      detail && typeof detail === 'object' && typeof detail.field === 'string'
        ? detail.field
        : undefined,
    limit:
      detail && typeof detail === 'object' && typeof detail.limit === 'number'
        ? detail.limit
        : undefined,
  };
}

/**
 * Validate a single field with the Phase 7 "optional-when-empty" rule:
 * - If `required` is `true` and the trimmed value is empty, returns
 *   `{ ok: false, message: '<required-msg>' }`.
 * - If `required` is `false` and the trimmed value is empty, returns
 *   `{ ok: true }` (the backend will skip-validate empty optional fields).
 * - Otherwise delegates to `validateText`.
 *
 * @param {string} value
 * @param {string} profile  one of the Phase 5 profile names
 * @param {{required?: boolean, requiredMessage?: string}=} options
 * @returns {{ok: boolean, message?: string, error_code?: string}}
 */
export function isFieldOk(value, profile, options) {
  const required = !options || options.required !== false;
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (trimmed === '') {
    if (!required) return { ok: true };
    return {
      ok: false,
      error_code: 'INPUT_REQUIRED',
      message: (options && options.requiredMessage) || 'Field is required.',
    };
  }
  return validateText(value, profile);
}

/**
 * Compute the submit-disable boolean for a form.
 *
 * @param {Object} args
 * @param {boolean} args.loading
 * @param {Array<{value: string, profile: string, required?: boolean}>} args.fields
 * @returns {boolean}  true when the submit button must be disabled.
 */
export function applySubmitGate(args) {
  if (!args || args.loading) return true;
  const fields = Array.isArray(args.fields) ? args.fields : [];
  for (const f of fields) {
    if (!isFieldOk(f.value, f.profile, { required: f.required !== false }).ok) {
      return true;
    }
  }
  return false;
}
